<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="modalTitle"
    :steps="wizardSteps"
    size="medium"
    :reset-on-close="true"
    :loading="isSaving"
    @cancel="handleClose"
    @finish="handleFinish"
    @close="handleClose"
  >
    <!-- Step 0: Basic Info -->
    <template #step-0>
      <div class="wizard-step-content">
        <!-- Franchise Selection -->
        <div class="form-row">
          <FranchiseSelect
            v-model="formData.franchise_id"
            elem-id="deduction-franchise"
            :label="t('Franquia')"
            :disabled="isEditMode"
            @update:modelValue="handleFranchiseChange"
          />
        </div>

        <!-- Title -->
        <div class="form-row">
          <input-text
            v-model="formData.title"
            data-testid="deduction-titulo-input"
            elem-id="deduction-title"
            :label="t('Título')"
            :placeholder="t('Ex: Dano em equipamento')"
            :required="true"
            :validation="() => formData.title && formData.title.trim() !== ''"
          />
        </div>

        <!-- Amount -->
        <div class="form-row">
          <InputCurrency
            v-model="formData.amount"
            elem-id="deduction-amount"
            :label="t('Valor')"
            ng-required="true"
            :validation="() => formData.amount > 0"
          />
        </div>
      </div>
    </template>

    <!-- Step 1: Employee Selection -->
    <template #step-1>
      <div class="wizard-step-content">
        <h3 class="step-title">{{ t('Selecione o colaborador') }}:</h3>

        <div v-if="loadingUsers" class="loading-container">
          <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
          <p>{{ t('Carregando usuários...') }}</p>
        </div>

        <Message v-else-if="userOptions.length === 0" severity="warn">
          <template #icon>
            <i class="pi pi-exclamation-triangle"></i>
          </template>
          {{ t('Nenhum usuário disponível para esta franquia') }}
        </Message>

        <ButtonsList
          v-else
          :itens-select="userOptions"
          :value="formData.employee_id"
          @input="formData.employee_id = $event"
          elem-id="deduction_employee"
          :search-enabled="userOptions.length > 5"
        />
      </div>
    </template>

    <!-- Step 2: Description -->
    <template #step-2>
      <div class="wizard-step-content">
        <!-- Description (Rich Text) -->
        <div class="form-row">
          <InputRichText
            v-model="formData.description"
            elem-id="deduction-description"
            :label="t('Descrição')"
            :placeholder="t('Descreva o motivo da dedução (opcional)')"
            :show-toolbar="true"
            min-height="120px"
          />
        </div>

        <!-- Delete Button (only for edit mode with open status) -->
        <div v-if="isEditMode && deductionData?.status === 1" class="delete-section">
          <Button
            data-testid="deduction-excluir-btn"
            :label="t('Excluir dedução')"
            icon="pi pi-trash"
            severity="danger"
            size="small"
            @click="handleDelete"
          />
        </div>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { useEmployeeDeductions } from '../../../composables/useEmployeeDeductions';
import { useConfirmAction } from '../../../composables/useConfirmAction';
import { useDateFormatter } from '../../../composables/useDateFormatter';
import { permissionsService } from '../../../services/permissionsService';

type Id = number | string;

interface DeductionPayload {
  id?: Id;
  franchise_id?: Id | null;
  employee_id?: Id | null;
  title?: string;
  amount?: number;
  description?: string;
  status?: number;
  [key: string]: unknown;
}

interface FormData {
  franchise_id: Id | null;
  employee_id: Id | null;
  title: string;
  amount: number;
  description: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserOption = any;

const { t } = useI18n();
const { createDeduction, updateDeduction, deleteDeduction, getDeduction, error } = useEmployeeDeductions();
const { confirmAction } = useConfirmAction();
const { fromDateToApiDate: _fromDateToApiDate } = useDateFormatter();

// State
const isVisible = ref<boolean>(false);
const deductionId = ref<Id | null>(null);
const deductionData = ref<DeductionPayload | null>(null);
const isSaving = ref<boolean>(false);
const loadingUsers = ref<boolean>(false);
const userOptions = ref<UserOption[]>([]);

// Form data
const formData = ref<FormData>({
  franchise_id: null,
  employee_id: null,
  title: '',
  amount: 0,
  description: ''
});

// Computed
const isEditMode = computed((): boolean => {
  return !!deductionId.value || !!deductionData.value?.id;
});

const modalTitle = computed((): string => {
  return isEditMode.value
    ? t('Editar Dedução')
    : t('Nova Dedução');
});

// Step validation functions
const validateStep0 = (): boolean => {
  return !!(formData.value.franchise_id &&
         formData.value.title &&
         formData.value.title.trim() !== '' &&
         formData.value.amount > 0);
};

const validateStep1 = (): boolean => {
  return !!formData.value.employee_id;
};

const validateStep2 = (): boolean => {
  // Description is optional, always valid
  return true;
};

// Wizard steps configuration
const wizardSteps = computed(() => [
  {
    title: t('Informações'),
    description: t('Dados básicos da dedução'),
    validate: validateStep0
  },
  {
    title: t('Colaborador'),
    description: t('Quem receberá a dedução'),
    validate: validateStep1
  },
  {
    title: t('Descrição'),
    description: t('Detalhes adicionais'),
    validate: validateStep2
  }
]);

// Methods
const resetForm = (): void => {
  formData.value = {
    franchise_id: null,
    employee_id: null,
    title: '',
    amount: 0,
    description: ''
  };
  deductionId.value = null;
  deductionData.value = null;
  userOptions.value = [];
};

/**
 * Handle franchise selection change - load users for the selected franchise
 */
const handleFranchiseChange = (franchiseId: Id | null): void => {
  log('EmployeeDeductionsModal', 'handleFranchiseChange', 'Franchise changed', franchiseId);

  // Reset user selection when franchise changes
  formData.value.employee_id = null;

  if (franchiseId) {
    loadUsersForFranchise(franchiseId);
  } else {
    userOptions.value = [];
  }
};

/**
 * Load users for a specific franchise using permissionsService
 */
const loadUsersForFranchise = (franchiseId: Id): void => {
  loadingUsers.value = true;

  try {
    log('EmployeeDeductionsModal', 'loadUsersForFranchise', 'Loading users for franchise', franchiseId);

    // Get operadores (and all users) for the franchise
    const operadores = permissionsService.getOperadoresSelectArrayByFranquiaID(
      typeof franchiseId === 'string' ? parseInt(franchiseId) : franchiseId
    );

    userOptions.value = operadores;
    log('EmployeeDeductionsModal', 'loadUsersForFranchise', 'Loaded users', userOptions.value.length);
  } catch (err) {
    log('EmployeeDeductionsModal', 'loadUsersForFranchise', 'Error loading users', err);
    userOptions.value = [];
  } finally {
    loadingUsers.value = false;
  }
};

const handleModalOpen = async (data: DeductionPayload | null = null): Promise<void> => {
  log('EmployeeDeductionsModal', 'handleModalOpen', 'Opening modal', data);

  resetForm();

  if (data?.id) {
    // Edit mode - load from data or fetch
    deductionId.value = data.id;
    deductionData.value = data;

    // Check if we have enough data to populate the form
    const hasFullData = data.title || (data.franchise_id && data.amount);

    log('EmployeeDeductionsModal', 'handleModalOpen', 'Data check', {
      hasFullData,
      hasTitle: !!data.title,
      hasFranchiseAndAmount: !!(data.franchise_id && data.amount),
      dataKeys: Object.keys(data || {})
    });

    if (hasFullData) {
      // Use the provided data
      formData.value = {
        franchise_id: (data.franchise_id as Id | null) ?? null,
        employee_id: (data.employee_id as Id | null) ?? null,
        title: (data.title as string) || '',
        amount: (data.amount as number) || 0,
        description: (data.description as string) || ''
      };
      log('EmployeeDeductionsModal', 'handleModalOpen', 'Form data populated from provided data', formData.value);

      // Load users for the franchise if set
      if (data.franchise_id) {
        loadUsersForFranchise(data.franchise_id as Id);
      }
    } else {
      // Fetch full data from API
      log('EmployeeDeductionsModal', 'handleModalOpen', 'Fetching full deduction data from API', data.id);
      const deduction = await getDeduction(data.id);
      if (deduction) {
        log('EmployeeDeductionsModal', 'handleModalOpen', 'Deduction data fetched', deduction);

        formData.value = {
          franchise_id: deduction.franchise_id ?? null,
          employee_id: deduction.employee_id ?? null,
          title: deduction.title || '',
          amount: deduction.amount || 0,
          description: deduction.description || ''
        };
        // Load users for the franchise if set
        if (deduction.franchise_id) {
          loadUsersForFranchise(deduction.franchise_id);
        }
      } else {
        log('EmployeeDeductionsModal', 'handleModalOpen', 'Failed to fetch deduction data', data.id);
      }
    }
  } else if (data) {
    // New with initial data (e.g., pre-filled franchise)
    formData.value.franchise_id = (data.franchise_id as Id | null) ?? null;
    if (data.franchise_id) {
      loadUsersForFranchise(data.franchise_id as Id);
    }
  }

  isVisible.value = true;
};

const handleClose = (): void => {
  log('EmployeeDeductionsModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  resetForm();
};

const handleFinish = async (): Promise<void> => {
  log('EmployeeDeductionsModal', 'handleFinish', 'Finishing wizard', formData.value);

  isSaving.value = true;

  try {
    const payload = {
      title: formData.value.title,
      description: formData.value.description,
      amount: formData.value.amount,
      franchise_id: formData.value.franchise_id,
      employee_id: formData.value.employee_id,
      // The wizard has no date field; deduction_date is required by
      // Payroll_CreateEmployeeDeductionInput, so default it to today (DATE-only).
      deduction_date: _fromDateToApiDate(new Date())
    };

    let result: unknown;
    let errorMessage: string | null = null;

    if (isEditMode.value) {
      const id = deductionId.value || deductionData.value?.id;
      result = await updateDeduction(id, payload);
      if (!result) {
        errorMessage = (error.value as unknown as string) || t('Erro ao atualizar dedução');
      }
    } else {
      result = await createDeduction(payload);
      if (!result) {
        errorMessage = (error.value as unknown as string) || t('Erro ao criar dedução');
      }
    }

    if (result) {
      log('EmployeeDeductionsModal', 'handleFinish', 'Saved successfully', result);

      // Emit events
      eventBus.emit('employee-deductions.created');
      eventBus.emit('employee-deductions.updated');
      eventBus.emit('employee-deductions.refresh');

      // Show success message
      eventBus.emit('globalMessage.success', isEditMode.value
        ? t('Dedução atualizada com sucesso')
        : t('Dedução criada com sucesso'));

      handleClose();
    } else {
      // Show error from backend
      log('EmployeeDeductionsModal', 'handleFinish', 'Error from backend', errorMessage);
      eventBus.emit('globalMessage.error', errorMessage);
    }
  } catch (err) {
    log('EmployeeDeductionsModal', 'handleFinish', 'Error', err);
    const message = err instanceof Error ? err.message : t('Erro ao salvar dedução');
    eventBus.emit('globalMessage.error', message);
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = (): void => {
  const id = deductionId.value || deductionData.value?.id;
  if (!id) return;

  log('EmployeeDeductionsModal', 'handleDelete', 'Deleting deduction', id);

  confirmAction({
    message: t('Tem certeza que deseja excluir esta dedução?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const success = await deleteDeduction(id);
      if (success) {
        eventBus.emit('globalMessage.success', {
          message: t('Dedução excluída com sucesso'),
          duration: 3000
        });
        handleClose();
        eventBus.emit('employee-deductions.refresh');
      } else {
        eventBus.emit('globalMessage.error', {
          message: t('Erro ao excluir dedução'),
          duration: 5000
        });
      }
    }
  });
};

// Store handler ref to ensure matching on/off — fixes latent bug where anonymous arrow
// was registered in onMounted but could not be deregistered in onBeforeUnmount
const handleOpenModalNew = (): void => { handleModalOpen(null); };

// Event listeners
onMounted(() => {
  // Standard modal events
  eventBus.on('modal.employee-deductions.open', handleModalOpen);
  eventBus.on('modal.employee-deductions.close', handleClose);

  // Legacy/alternative events
  eventBus.on('employee-deductions.open-modal-new', handleOpenModalNew);
  eventBus.on('employee-deductions.open-modal-edit', handleModalOpen);
  eventBus.on('employee-deductions.edit', handleModalOpen);
});

onBeforeUnmount(() => {
  // Standard modal events
  eventBus.off('modal.employee-deductions.open', handleModalOpen);
  eventBus.off('modal.employee-deductions.close', handleClose);

  // Legacy/alternative events
  eventBus.off('employee-deductions.open-modal-new', handleOpenModalNew);
  eventBus.off('employee-deductions.open-modal-edit', handleModalOpen);
  eventBus.off('employee-deductions.edit', handleModalOpen);
});
</script>

<style scoped>
.wizard-step-content {
  padding: 0.5rem;
}

.step-title {
  margin-bottom: 1rem;
  font-weight: 500;
  color: var(--text-color);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
}

.form-row {
  margin-bottom: 1.25rem;
}

.delete-section {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-200);
  display: flex;
  justify-content: flex-end;
}
</style>
